import React from 'react';
import EducationCatalogPage from './EducationCatalog';
import { legalEntitiesAPI, businessUnitsAPI } from '../services/api';
import { Landmark, Boxes, CheckCircle2, Globe } from 'lucide-react';
import Swal from 'sweetalert2';

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

// Countries with first-class payroll support (mirrors backend SUPPORTED_COUNTRIES).
const COUNTRIES = ['IN', 'US', 'AE', 'SG', 'MY', 'GB', 'AU', 'CA'];

const triggerDownload = async (promise, filename) => {
  try {
    const res = await promise;
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    Swal.fire('Export failed', err?.response?.data?.detail || 'Please try again.', 'error');
  }
};

const activeEntityOptions = async () => {
  const res = await legalEntitiesAPI.list({ status: 'active' });
  return toArray(res).map((e) => ({ value: e.id, label: `${e.name} (${e.code}) · ${e.country}` }));
};

const LEGAL_ENTITY_CONFIG = {
  entity: 'legal-entity',
  entityLabel: 'Legal Entity',
  title: 'Legal Entities',
  subtitle: 'Registered companies in your group. Each carries the country & currency payroll runs in.',
  primaryKey: 'name',
  icon: Landmark,
  searchable: true,
  api: {
    list: (params) => legalEntitiesAPI.list(params),
    create: (d) => legalEntitiesAPI.create(d),
    update: (id, d) => legalEntitiesAPI.update(id, d),
    remove: (id) => legalEntitiesAPI.remove(id),
    importItems: (items) => legalEntitiesAPI.importItems(items),
    exportUrl: () => triggerDownload(legalEntitiesAPI.exportCsv(), 'legal_entities.csv'),
  },
  stats: (rows) => [
    { label: 'Legal Entities', value: rows.length, icon: Landmark },
    { label: 'Active', value: rows.filter((r) => r.is_active).length, icon: CheckCircle2 },
    { label: 'Countries', value: new Set(rows.map((r) => r.country)).size, icon: Globe },
    { label: 'Business Units', value: rows.reduce((n, r) => n + (r.business_unit_count || 0), 0), icon: Boxes },
  ],
  columns: [
    { key: 'code', label: 'Code', primary: true },
    { key: 'name', label: 'Name' },
    { key: 'country', label: 'Country' },
    { key: 'currency', label: 'Currency' },
    { key: 'business_unit_count', label: 'BUs' },
    { key: 'status', label: 'Status', status: true },
  ],
  fields: [
    { key: 'code', label: 'Code', required: true, placeholder: 'ACME-IN' },
    { key: 'name', label: 'Name', required: true, placeholder: 'Acme India' },
    { key: 'legal_name', label: 'Registered Legal Name', placeholder: 'Acme India Private Limited' },
    { key: 'country', label: 'Country', type: 'select', options: COUNTRIES },
    { key: 'currency', label: 'Currency', placeholder: 'auto from country if blank' },
    { key: 'registration_no', label: 'Registration No. (CIN/EIN/UEN)', placeholder: 'U72900KA…' },
    { key: 'tax_id', label: 'Tax ID (GSTIN/VAT/TRN)', placeholder: '29ABCDE…' },
    { key: 'timezone', label: 'Timezone', placeholder: 'Asia/Kolkata' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'address', label: 'Registered Address', type: 'textarea', full: true },
  ],
  empty: {
    code: '', name: '', legal_name: '', country: 'IN', currency: '', registration_no: '',
    tax_id: '', timezone: '', email: '', phone: '', address: '', display_order: 0, is_active: true,
  },
  numberFields: ['display_order'],
  viewExtras: [{ key: 'business_unit_count', label: 'Business Units' }],
  importColumns: ['code', 'name', 'legal_name', 'country', 'currency', 'registration_no', 'tax_id', 'is_active'],
  importSample: ['ACME-IN', 'Acme India', 'Acme India Private Limited', 'IN', 'INR', 'U72900KA2020PTC', '29ABCDE1234F1Z5', 'active'],
};

const BUSINESS_UNIT_CONFIG = {
  entity: 'business-unit',
  entityLabel: 'Business Unit',
  title: 'Business Units',
  subtitle: 'Operating units within a legal entity (e.g. Engineering, Retail-West).',
  primaryKey: 'name',
  icon: Boxes,
  searchable: true,
  filters: [
    { key: 'legal_entity_id', label: 'Legal entity', allLabel: 'All entities', optionsSource: activeEntityOptions },
  ],
  api: {
    list: (params) => businessUnitsAPI.list(params),
    create: (d) => businessUnitsAPI.create(d),
    update: (id, d) => businessUnitsAPI.update(id, d),
    remove: (id) => businessUnitsAPI.remove(id),
    importItems: (items) => businessUnitsAPI.importItems(items),
    exportUrl: () => triggerDownload(businessUnitsAPI.exportCsv(), 'business_units.csv'),
  },
  stats: (rows) => [
    { label: 'Business Units', value: rows.length, icon: Boxes },
    { label: 'Active', value: rows.filter((r) => r.is_active).length, icon: CheckCircle2 },
    { label: 'Legal Entities', value: new Set(rows.map((r) => r.legal_entity_id)).size, icon: Landmark },
  ],
  columns: [
    { key: 'legal_entity_name', label: 'Legal Entity', primary: true },
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Business Unit' },
    { key: 'head_name', label: 'Head' },
    { key: 'cost_center', label: 'Cost Center' },
    { key: 'status', label: 'Status', status: true },
  ],
  fields: [
    { key: 'legal_entity_id', label: 'Legal Entity', type: 'dynamic-select', required: true, displayKey: 'legal_entity_name', optionsSource: activeEntityOptions },
    { key: 'code', label: 'Code', required: true, placeholder: 'ENG' },
    { key: 'name', label: 'Name', required: true, placeholder: 'Engineering' },
    { key: 'head_name', label: 'BU Head', placeholder: 'Dr. R. Verma' },
    { key: 'cost_center', label: 'Cost Center', placeholder: 'CC-1001' },
    { key: 'description', label: 'Description', type: 'textarea', full: true },
  ],
  empty: { legal_entity_id: '', code: '', name: '', head_name: '', cost_center: '', description: '', display_order: 0, is_active: true },
  numberFields: ['legal_entity_id', 'display_order'],
  importColumns: ['entity_code', 'code', 'name', 'head_name', 'cost_center', 'is_active'],
  importSample: ['ACME-IN', 'ENG', 'Engineering', 'Dr. R. Verma', 'CC-1001', 'active'],
};

export const LegalEntitiesPage = () => <EducationCatalogPage config={LEGAL_ENTITY_CONFIG} />;
export const BusinessUnitsPage = () => <EducationCatalogPage config={BUSINESS_UNIT_CONFIG} />;
